import { api } from '../../core/network/api';
import { createDeposit as coreCreateDeposit } from '../../core/payments/payments-api';
import type { CreateDepositResponse, DepositMethodId } from '../../core/payments/types';

/**
 * The Ember storefront's REST surface — plain wrappers, same convention as
 * `energy-api.ts`.
 *
 * Every number here is the server's. The client never converts euros to Ember
 * for itself: Formula 5 is an economy rule, and a rate the browser works out
 * is a rate that can disagree with what the payment webhook actually credits.
 */

export interface EmberPackRow {
  id: string;
  eurCents: number;
  ember: number;
}

export interface EmberOffers {
  /** False when this deployment has no payment provider registered. */
  purchasable: boolean;
  emberPerEur: number;
  packs: EmberPackRow[];
  custom: { minCents: number; maxCents: number; stepCents: number };
  /** Ember that may be exchanged into Wax per rolling day, whatever you buy. */
  dailyExchangeCapPremium: number;
}

export const getEmberOffers = (): Promise<EmberOffers> => api.get('/economy/ember/offers');

export const createEmberDeposit = (
  opts: { productId?: string; amountCents?: number; method?: DepositMethodId; currency?: string },
): Promise<CreateDepositResponse> =>
  coreCreateDeposit({
    method: opts.method ?? 'card',
    currency: opts.currency ?? 'EUR',
    ...(opts.productId ? { productId: opts.productId } : {}),
    ...(opts.amountCents ? { amountCents: opts.amountCents } : {}),
    returnPath: '/play',
  });

/**
 * The limited-time lobby offer. `bonusEmber` is granted on the payment
 * webhook, once per player — the server re-checks all of it at checkout.
 */
export interface EmberLobbyOffer {
  id: string;
  eurCents: number;
  bonusPct: number;
  baseEmber: number;
  bonusEmber: number;
  totalEmber: number;
  /** ISO timestamp; the countdown is computed from it, never ticked server-side. */
  endsAt: string;
}

/** `offer: null` means there is nothing to show — off, over, taken or unpayable. */
export const getEmberLobbyOffer = (): Promise<{ offer: EmberLobbyOffer | null }> =>
  api.get('/economy/ember/offer');
